import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  PRIVACY_INTRO,
  PRIVACY_SECTIONS,
  TERMS_INTRO,
  TERMS_SECTIONS,
} from '../lib/legalDocuments';

function SectionBlock({ section }) {
  return (
    <section className="space-y-3">
      <h4 className="text-sm font-bold text-white">{section.title}</h4>
      {(section.paragraphs || []).map((paragraph) => (
        <p key={paragraph} className="text-sm leading-relaxed text-white/70">
          {paragraph}
        </p>
      ))}
      {Array.isArray(section.bullets) && section.bullets.length > 0 ? (
        <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-white/70">
          {section.bullets.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      {(section.afterBullets || []).map((paragraph) => (
        <p key={paragraph} className="text-sm leading-relaxed text-white/70">
          {paragraph}
        </p>
      ))}
    </section>
  );
}

function AccordionPanel({ id, title, subtitle, open, onToggle, children }) {
  return (
    <div className="rounded-[18px] border border-white/10 bg-[#232323] overflow-hidden">
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-3 px-4 py-4 text-left transition active:scale-[0.99]"
      >
        <span>
          <span className="block text-[14px] font-semibold text-white">{title}</span>
          {subtitle ? (
            <span className="mt-1 block text-[12px] leading-relaxed text-white/55">{subtitle}</span>
          ) : null}
        </span>
        <ChevronDown
          size={18}
          className={`mt-0.5 shrink-0 text-white/60 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <div className="border-t border-white/10 px-4 py-4 space-y-4">
          {children}
        </div>
      ) : null}
    </div>
  );
}

export default function TermsPolicyContent() {
  const [openPanel, setOpenPanel] = useState('');

  const togglePanel = (id) => {
    setOpenPanel((prev) => (prev === id ? '' : id));
  };

  return (
    <div className="space-y-3">
      <AccordionPanel
        id="terms"
        title="Terms of Use"
        subtitle="Rules for using Echo Jham"
        open={openPanel === 'terms'}
        onToggle={togglePanel}
      >
        <p className="text-sm leading-relaxed text-white/70">{TERMS_INTRO}</p>
        {TERMS_SECTIONS.map((section) => (
          <SectionBlock key={section.id} section={section} />
        ))}
      </AccordionPanel>

      <AccordionPanel
        id="privacy"
        title="Privacy Policy"
        subtitle="How we handle your information"
        open={openPanel === 'privacy'}
        onToggle={togglePanel}
      >
        <p className="text-sm leading-relaxed text-white/70">{PRIVACY_INTRO}</p>
        {PRIVACY_SECTIONS.map((section) => (
          <SectionBlock key={section.id} section={section} />
        ))}
      </AccordionPanel>

      <div className="rounded-[18px] border border-white/10 bg-[#232323] px-4 py-5 space-y-2">
        <h3 className="text-[14px] font-semibold text-white">Need help?</h3>
        <p className="text-[13px] leading-relaxed text-white/70">
          If you have questions about your privacy, account, or use of our services, contact our support team.
        </p>
        <p className="text-[13px] leading-relaxed text-white/70">
          Contact Support via the{' '}
          <Link to="/profile/help" className="font-semibold text-[#c5a059] hover:underline">
            Supports Page
          </Link>{' '}
          inside the app or contact us directly through email{' '}
          <a
            href="mailto:echojhamproductions@gmail.com"
            className="font-semibold text-[#c5a059] hover:underline break-all"
          >
            echojhamproductions@gmail.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}
